import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah } from "@/lib/constants";
import { FileBarChart, Clock, LogIn, LogOut, Wrench, Receipt, Landmark, ChevronRight } from "lucide-react";

const PERIODS = [["day", "Harian"], ["week", "Mingguan"], ["month", "Bulanan"], ["custom", "Custom"]];

function Shell({ children }) {
  return (
    <div className="ti-shell -mx-3 -my-3 min-h-[calc(100vh-3.25rem)] px-3 py-5 sm:-mx-5 sm:-my-5 sm:px-6 sm:py-6">
      <div className="mx-auto w-full max-w-[1500px]">{children}</div>
    </div>
  );
}

function Metric({ label, value, money, delta }) {
  return (
    <div className="ti-card p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider ti-muted">{label}</div>
      <div className="mt-1 font-heading text-xl font-bold text-white">{money ? formatRupiah(value) : value}</div>
      {delta !== undefined && delta !== null && (
        <div className={`mt-0.5 text-[11px] font-semibold ${delta > 0 ? "text-emerald-400" : delta < 0 ? "text-red-400" : "ti-muted"}`}>
          {delta > 0 ? "▲" : delta < 0 ? "▼" : "•"} {Math.abs(delta)} vs minggu lalu
        </div>
      )}
    </div>
  );
}

function AgingCard({ title, subtitle, data, icon: Icon }) {
  const navigate = useNavigate();
  if (!data) return null;
  return (
    <div data-testid={`aging-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="ti-card p-4">
      <div className="flex items-center gap-2">
        <span className="ti-glow-icon flex h-8 w-8 items-center justify-center rounded-lg"><Icon size={15} /></span>
        <div><h3 className="font-heading text-sm font-bold text-white">{title}</h3><div className="text-[10px] ti-muted">{subtitle}</div></div>
      </div>
      <div className="mt-3 grid grid-cols-4 gap-2 text-center">
        <div><div className="font-heading text-lg font-bold text-white">{data.avg}h</div><div className="text-[9px] uppercase ti-muted">Avg</div></div>
        <div><div className="font-heading text-lg font-bold text-[#C7D2E5]">{data.count}</div><div className="text-[9px] uppercase ti-muted">Unit</div></div>
        <div><div className="font-heading text-lg font-bold text-emerald-400">{data.on_target}</div><div className="text-[9px] uppercase ti-muted">On Target</div></div>
        <div><div className="font-heading text-lg font-bold text-red-400">{data.overdue}</div><div className="text-[9px] uppercase ti-muted">Overdue</div></div>
      </div>
      <div className="mt-3 space-y-1">
        {(data.items || []).slice(0, 6).map((it, i) => (
          <button key={i} data-testid={`aging-item-${title.toLowerCase().replace(/[^a-z]+/g, "-")}-${i}`}
            onClick={() => navigate(`/claims?search=${encodeURIComponent(it.wo || it.nopol || it.claim_no)}`)}
            className="flex w-full items-center gap-2 rounded-md border border-blue-500/10 bg-white/5 px-2 py-1.5 text-left transition-colors hover:bg-white/10">
            <span className="ti-plate text-[10px]">{it.nopol || "—"}</span>
            <span className="font-mono text-[10px] font-bold text-[#60A5FA]">{it.wo || "WO—"}</span>
            <span className="ml-auto text-xs font-bold text-white">{it.days}h</span>
            <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${it.days > data.target_days ? "bg-red-500/20 text-red-300" : "bg-emerald-500/15 text-emerald-300"}`}>{it.status}</span>
            <ChevronRight size={13} className="ti-muted" />
          </button>
        ))}
        {(data.items || []).length === 0 && <p className="py-2 text-center text-xs ti-muted">Tidak ada data.</p>}
      </div>
    </div>
  );
}

export default function ReportsCenter() {
  const [period, setPeriod] = useState("day");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    try {
      const params = { period };
      if (period === "custom") { params.date_from = from; params.date_to = to; }
      const { data } = await api.get("/reports/summary", { params });
      setD(data);
    } catch (e) { setD(null); }
  }, [period, from, to]);
  useEffect(() => { if (period !== "custom" || (from && to)) load(); }, [load, period, from, to]);

  const op = d?.operations || {};
  const fin = d?.finance || {};
  const pw = d?.prev_week;
  const delta = (cur, key) => (pw ? (cur - (pw[key] ?? 0)) : undefined);

  return (
    <Shell>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#60A5FA]"><FileBarChart size={13} /> Reports</div>
          <h1 className="mt-1 font-heading text-2xl font-extrabold text-white sm:text-3xl">Laporan Otomatis</h1>
          <p className="mt-1 text-sm ti-muted">Daily · Weekly · Monthly — dihitung langsung dari database (tanpa dummy).{d?.window ? ` Window: ${d.window.from} → ${d.window.to}` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PERIODS.map(([k, l]) => (
            <button key={k} data-testid={`report-period-${k}`} onClick={() => setPeriod(k)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${period === k ? "ti-gold-bg text-[#04101F]" : "ti-card ti-muted"}`}>{l}</button>
          ))}
        </div>
      </div>

      {period === "custom" && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input data-testid="report-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 rounded-lg border border-blue-500/25 bg-[#0A1424] px-3 text-sm text-[#E7ECF3]" />
          <span className="ti-muted">→</span>
          <input data-testid="report-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 rounded-lg border border-blue-500/25 bg-[#0A1424] px-3 text-sm text-[#E7ECF3]" />
        </div>
      )}

      <h2 className="mb-2 mt-6 flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-[0.2em] text-[#C7D2E5]"><span className="h-4 w-1 rounded-full ti-gold-bg" />Operasional</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Metric label="Unit Masuk" value={op.unit_masuk ?? 0} delta={delta(op.unit_masuk ?? 0, "unit_masuk")} />
        <Metric label="Unit Keluar" value={op.unit_keluar ?? 0} delta={delta(op.unit_keluar ?? 0, "unit_keluar")} />
        <Metric label="Unit Aktif" value={op.unit_aktif ?? 0} />
        <Metric label="Pre Order" value={op.pre_order ?? 0} />
        <Metric label="Repair Order" value={op.repair_order ?? 0} />
        <Metric label="Finish Order" value={op.finish_order ?? 0} />
        <Metric label="WO Created" value={op.wo_created ?? 0} delta={delta(op.wo_created ?? 0, "wo_created")} />
        <Metric label="Production Output" value={op.production_output ?? 0} />
        <Metric label="Pending Sparepart" value={op.pending_sparepart ?? 0} />
        <Metric label="Ready Delivery" value={op.ready_delivery ?? 0} />
        <Metric label="Invoice Created" value={op.invoice_created ?? 0} delta={delta(op.invoice_created ?? 0, "invoice_created")} />
        <Metric label="Nilai Invoice" value={op.invoice_value ?? 0} money />
      </div>

      <h2 className="mb-2 mt-6 flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-[0.2em] text-[#C7D2E5]"><span className="h-4 w-1 rounded-full ti-gold-bg" />Keuangan</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric label="Cash In" value={fin.cash_in ?? 0} money />
        <Metric label="Cash Out" value={fin.cash_out ?? 0} money />
        <Metric label="Piutang" value={fin.piutang ?? 0} money />
        <Metric label="Hutang" value={fin.hutang ?? 0} money />
      </div>

      <h2 className="mb-2 mt-6 flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-[0.2em] text-[#C7D2E5]"><span className="h-4 w-1 rounded-full ti-gold-bg" />Aging (Terpisah 3 Tahap)</h2>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <AgingCard title="Pre Order Aging" subtitle="Claim / SPK → Unit In · target ≤3h" data={d?.aging?.pre_order} icon={LogIn} />
        <AgingCard title="Production Aging" subtitle="Unit In → Unit Out · target ≤5h" data={d?.aging?.production} icon={Wrench} />
        <AgingCard title="Collection Aging" subtitle="Invoice → Paid / Closed · target ≤30h" data={d?.aging?.collection} icon={Landmark} />
      </div>
      {!d && <p className="py-10 text-center text-sm ti-muted">Pilih rentang custom atau tunggu data dimuat…</p>}
    </Shell>
  );
}
